import { useEffect } from 'react';

import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import { loginByEmailCode, loginByMagicToken } from '@realadvisor/auth-client';
import { Image } from '@realadvisor/image';
import { MailCheck } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useLocale } from '@shared/hooks/locale';

/**
 * Public route that handles magic-link email login.
 *
 * URL formats:
 *   /:lang/login-email?code=XXXXX&email=user@example.com&redirect_uri=https://...
 *     Built by the auth service for the claim/takedown links agency-pages
 *     sends. Those links always carry a tenant_id, so the session established
 *     here is never a multitenant one. The code lives 5 minutes.
 *   /:lang/login-email?token=<magic token>
 *     The sign-in link of the paid-client onboarding emails: a 7-day magic
 *     token from provision_paid_customer, exchanged like an invitation's. The
 *     tenant comes from the CRM host.
 *
 * CRM login itself emails a code with no link. After verifying, reloads the
 * page so the CRM re-initialises with the new session, then navigates to
 * redirect_uri (the CRM home, and so the onboarding wizard, without one).
 */

// Module-level guard: survives React Strict Mode double-invocation and Vite
// HMR remounts. Keyed by code or token so a fresh magic link always works.
const attemptedCodes = new Set<string>();

export const LoginEmail = () => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();

  const redirectUri = searchParams.get('redirect_uri');
  const code = searchParams.get('code');
  const email = searchParams.get('email');
  const token = searchParams.get('token');
  const attempt = token || (code && email ? code : null);

  useEffect(() => {
    if (attempt == null || attemptedCodes.has(attempt)) {
      return;
    }
    attemptedCodes.add(attempt);

    // Clear any existing session before processing the magic link so that
    // loginByEmailCode always establishes a clean session for the new user
    // (it forwards the current refresh_token if present). Keep the old tokens
    // aside so a dead link doesn't log out whoever was signed in.
    const previousAccessToken = localStorage.getItem('access_token');
    const previousRefreshToken = localStorage.getItem('refresh_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    const restorePreviousSession = () => {
      // Only restore when the login left no new session behind. loginByEmailCode
      // rotates refresh_token *before* it can throw (e.g. an unparseable body
      // after a 200 + x-refresh-token), so a present refresh_token means a fresh
      // session was already established: restoring the old one would clobber it
      // and strand the user. A network error leaves it absent → safe to restore.
      if (localStorage.getItem('refresh_token') != null) {
        return;
      }
      if (previousAccessToken != null) {
        localStorage.setItem('access_token', previousAccessToken);
      }
      if (previousRefreshToken != null) {
        localStorage.setItem('refresh_token', previousRefreshToken);
      }
    };

    // A dead link is the same situation as typing an expired code, so send the
    // user back to the login card with the error it already knows how to show,
    // rather than to /noaccess. A token link lives days, not 5 minutes.
    const redirectToLogin = () => {
      const loginUrl = new URL('/login', window.location.origin);
      loginUrl.searchParams.set(
        'error',
        token ? 'EXPIRED_LINK' : 'EXPIRED_CODE',
      );
      if (redirectUri != null) {
        loginUrl.searchParams.set('return', redirectUri);
      }
      window.location.replace(loginUrl.href);
    };

    const login = token ? loginByMagicToken() : loginByEmailCode(email ?? '');
    login
      .then(response => {
        if (!response.ok) {
          // e.g. 401 — expired, already-used or exhausted OTP code, or an
          // expired or unreadable magic token.
          restorePreviousSession();
          redirectToLogin();
          return;
        }
        // Reload so AppDataProvider re-fetches the authenticated session.
        // Then navigate to redirect_uri (e.g. /plans or /takedown?slug=...).
        // Guard against open redirect: only allow same-origin destinations.
        const raw = redirectUri ?? '';
        const destination =
          raw.startsWith(window.location.origin + '/') ||
          raw === window.location.origin
            ? raw
            : window.location.origin;

        window.location.replace(destination);
      })
      .catch(() => {
        restorePreviousSession();
        redirectToLogin();
      });
  }, [attempt, email, token, redirectUri]);

  if (attempt == null) {
    return (
      <Container
        maxWidth="xs"
        sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}
      >
        <Paper
          elevation={4}
          sx={{ p: 5, width: '100%', textAlign: 'center', borderRadius: 3 }}
        >
          <Box sx={{ margin: '0 auto', mb: 4, width: 200 }}>
            <Image
              src="/realadvisor-logo.svg"
              alt="RealAdvisor"
              options={{ w: 200 }}
            />
          </Box>
          <Alert severity="error">
            {t('Invalid magic link: missing code or email.')}
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xs"
      sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}
    >
      <Paper
        elevation={4}
        sx={{
          p: 5,
          width: '100%',
          textAlign: 'center',
          borderRadius: 3,
        }}
      >
        <Box sx={{ margin: '0 auto', mb: 4, width: 200 }}>
          <Image
            src="/realadvisor-logo.svg"
            alt="RealAdvisor"
            options={{ w: 200 }}
          />
        </Box>

        {/* The spinner sits inside the mail badge so the wait reads as one
            object rather than a stray loader above the text. */}
        <Box
          sx={{
            position: 'relative',
            width: 72,
            height: 72,
            margin: '0 auto',
            mb: 3,
          }}
        >
          <CircularProgress
            size={72}
            thickness={2}
            sx={{ position: 'absolute', inset: 0, color: 'primary.main' }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 8,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
              color: 'primary.main',
            }}
          >
            <MailCheck size={28} strokeWidth={1.8} />
          </Box>
        </Box>

        <Typography variant="h6" gutterBottom>
          {t('Logging you in…')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('You will be redirected automatically.')}
        </Typography>

        {email != null && (
          <Box
            sx={{
              mt: 3,
              px: 1.5,
              py: 0.5,
              display: 'inline-block',
              maxWidth: '100%',
              borderRadius: 999,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
              fontSize: 13,
              fontWeight: 600,
              overflowWrap: 'anywhere',
            }}
          >
            {email}
          </Box>
        )}
      </Paper>
    </Container>
  );
};
